import { useSDK, useSDKState } from '@littlebird/sdk';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { motion, type Variants } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { FLUTTER_DURATION_MS } from '@/assets/icons/useLogoFlutter';
import { FailedSavesBanner } from '@/components/blocks/FailedSavesBanner';
import { Main } from '@/components/blocks/Layout/Main';
import { SharedMarkdownEditorSingleton } from '@/components/ui/editor';
import { Splash } from '@/components/ui/Splash';
import { isE2ETestEnv } from '@/env';
// A deep import: the feature's barrel also exports the playground screen
import { useActionCardTemplates } from '@/features/action-cards-view/useActionCardTemplates';
import { CommandBar } from '@/features/cmd-bar/cmd-bar';
import { useRegisterDevice } from '@/features/devices/useRegisterDevice';
import {
  FEATURES,
  featureFlagsReady,
  hasFeature,
  refreshFeatureFlags,
} from '@/features/feature-flags';
import { MeetingGlobalEffects } from '@/features/meetings/meetings-global.effects';
import {
  shouldShowWebOnboarding,
  useCreateWebOnboardingThreads,
} from '@/features/onboarding-web';
// A deep import: the feature's barrel also exports the Wiki screens
import { usePeopleWikiChangedEvents } from '@/features/people-wiki/use-people-wiki-changed-events';
import { useProjectsChangedEvents } from '@/features/projects/use-projects-changed-events';
import { usePushNotifications } from '@/features/push-notifications/usePushNotifications';
import { useRoutinesUpdatedEvents } from '@/features/routines/useRoutinesUpdatedEvents';
import { ChatGptImportWatcher } from '@/features/settings/sub-settings/chatgpt-import/chatgpt-import-watcher';
import { useSettingsHashRedirect } from '@/features/settings/useSettingsHashRedirect';
import { subscriptionApis, useUsagePaceToasts } from '@/features/subscription';
import { useBillingUpdatedEvents } from '@/features/subscription/hooks/use-billing-updated-events';
import { useCheckoutReturnLanding } from '@/features/subscription/hooks/use-checkout-return-landing';
import { DiscoverableTeamBanner } from '@/features/team/components/DiscoverableTeamBanner';
import { teamApis } from '@/features/team/team.query';
import { MeadowAppFrame } from '@/features/web-download';
import { getPostAuthLandingRoute } from '@/features/welcome';
import { WhatsNewProvider } from '@/features/whats-new';
import { useGlobalEventListeners } from '@/hooks/useGlobalEventListeners';
import { useModalHash } from '@/hooks/useModalHash';
import { usePaywallStateEvents } from '@/hooks/usePaywallStateEvents';
import { useSetChatMode } from '@/hooks/useSetChatMode';
import { logger } from '@/lib/logger';
import { queryClient } from '@/query/client';
import { checkAccessibilityPermissionsQueryOptions } from '@/query/permissions';
import { USER_QUERY_KEY, userQueryOptions } from '@/query/user';
import { ErrorScreen } from '@/screens/app/ErrorScreen';
import { updateStore } from '@/stores/updateStore';
import { userStore } from '@/stores/user.store';
import { Routes } from '@/types/routes';
import { trackEvent } from '@/utils/analytics';
import { AppCapability, inElectron, isCapable } from '@/utils/capabilities';

export const Route = createFileRoute('/_authenticated')({
  component: () => <AuthenticatedLayout />,
  pendingComponent: () => <Splash reason="route-pending-auth-loader" />,
  pendingMs: 0,
  staticData: { requiresAuth: true },

  async beforeLoad({ context, location }) {
    const startedAt = Date.now();
    logger.info('Authenticated route beforeLoad started', {
      pathname: location.pathname,
    });
    await context.auth.requireAuth(location);
    logger.info('Authenticated route beforeLoad finished', {
      pathname: location.pathname,
      durationMs: Date.now() - startedAt,
    });
  },

  async loader() {
    const startedAt = Date.now();
    logger.info('Authenticated route loader started', {
      hasUserInStore: Boolean(userStore.user),
    });

    // userStore.user is saved in localStorage and can outlive API changes: a
    // profile saved before the welcome-page fields shipped will not have them.
    // Welcome reads them in route loaders, before the layout refresh runs.
    if (
      !userStore.user ||
      userStore.user.is_welcome_page_eligible === undefined ||
      userStore.user.posthog_feature_flags === undefined
    ) {
      const userLoadStartedAt = Date.now();
      // Stale persisted profiles may lack is_welcome_page_eligible; bypass any
      // in-memory React Query cache so the loader sees the server value.
      await queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
      const user = await queryClient.fetchQuery(userQueryOptions());
      if (!user) throw new Error('fatal, no user data');
      userStore.setUser(user);
      logger.info('Authenticated route loader fetched user', {
        durationMs: Date.now() - userLoadStartedAt,
        isOnboarded: userStore.isOnboarded,
      });
    }

    const needsOnboarding =
      (!userStore.isOnboarded && isCapable(AppCapability.Onboarding)) ||
      (await shouldShowWebOnboarding());
    if (needsOnboarding) {
      logger.info('Authenticated route loader redirecting to onboarding');
      trackEvent('FORCE_REDIRECT', {
        to: Routes.Onboarding as string,
        from: '/_authenticated/',
      });
      throw redirect({
        to: Routes.Onboarding as string,
        replace: true,
      });
    }
    if (isCapable(AppCapability.DesktopMainWindowExclusive) && !isE2ETestEnv) {
      const accessibilityStartedAt = Date.now();
      const hasAccessibilityPermissions = await queryClient.fetchQuery(
        checkAccessibilityPermissionsQueryOptions()
      );

      logger.info('Authenticated route loader checked accessibility', {
        durationMs: Date.now() - accessibilityStartedAt,
        hasAccessibilityPermissions,
      });

      if (!hasAccessibilityPermissions) {
        await featureFlagsReady();
        if (hasFeature(FEATURES.ONBOARDING_ACCESSIBILITY_SKIP)) {
          logger.info(
            'Authenticated route loader skipped accessibility redirect',
            {
              featureFlag: FEATURES.ONBOARDING_ACCESSIBILITY_SKIP,
            }
          );
        } else {
          logger.info(
            'Authenticated route loader redirecting to accessibility'
          );
          trackEvent('FORCE_REDIRECT', {
            to: Routes.Accessibility,
            from: '/_authenticated/',
          });

          throw redirect({
            to: Routes.Accessibility as string,
            replace: true,
          });
        }
      }
    }

    const shouldRedirectHome =
      window.location.pathname === Routes.Home ||
      (inElectron() &&
        window.location.href.split('app.asar/dist/')?.[1] === 'index.html');
    const postAuthLandingRoute = getPostAuthLandingRoute();

    if (shouldRedirectHome) {
      logger.info(
        'Authenticated route loader redirecting to post-auth landing',
        {
          to: postAuthLandingRoute,
        }
      );
    }

    logger.info('Authenticated route loader finished', {
      durationMs: Date.now() - startedAt,
      isOnboarded: userStore.isOnboarded,
    });

    if (shouldRedirectHome) {
      throw redirect({
        to: postAuthLandingRoute as string,
        replace: true,
      });
    }
  },
});

function MainContent() {
  return (
    <motion.div
      key="content"
      variants={fadeVariants}
      initial={false}
      animate="animate"
      exit="exit"
      className="h-dvh w-full"
    >
      <MeadowAppFrame>
        <Main />
      </MeadowAppFrame>
      <SharedMarkdownEditorSingleton />
      <CommandBar />
      <MeetingGlobalEffects />
      <ChatGptImportWatcher />
      <WhatsNewProvider />
      <DiscoverableTeamBanner />
    </motion.div>
  );
}

const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.3 },
  },
};

function useMinVisible(active: boolean, ms: number): boolean {
  const [visible, setVisible] = useState(active);
  const startedAtRef = useRef<number | null>(active ? Date.now() : null);

  useEffect(() => {
    if (active) {
      if (startedAtRef.current === null) startedAtRef.current = Date.now();
      setVisible(true);
      return;
    }
    if (startedAtRef.current === null) {
      setVisible(false);
      return;
    }
    const remaining = startedAtRef.current + ms - Date.now();
    if (remaining <= 0) {
      startedAtRef.current = null;
      setVisible(false);
      return;
    }
    const timer = setTimeout(() => {
      startedAtRef.current = null;
      setVisible(false);
    }, remaining);
    return () => clearTimeout(timer);
  }, [active, ms]);

  return visible;
}

const AuthenticatedLayout = observer(function AuthenticatedLayout() {
  useSettingsHashRedirect();
  useModalHash();
  useGlobalEventListeners();

  const sdk = useSDK();
  const { isLoading, error, isHydrated } = useSDKState();
  const showSplash = useMinVisible(isLoading, FLUTTER_DURATION_MS);
  useCreateWebOnboardingThreads(!isLoading && !error);
  usePaywallStateEvents();
  useCheckoutReturnLanding();
  useUsagePaceToasts();
  useRoutinesUpdatedEvents();
  useBillingUpdatedEvents();
  useProjectsChangedEvents();
  usePeopleWikiChangedEvents();
  useActionCardTemplates();

  useEffect(() => {
    const initialize = async () => {
      const startedAt = Date.now();
      logger.info('Authenticated layout SDK initialize started', {
        sdkState: sdk.state,
        isHydrated: sdk.isHydrated,
      });

      try {
        await sdk.initialize();
        logger.info('Authenticated layout SDK initialize finished', {
          durationMs: Date.now() - startedAt,
          sdkState: sdk.state,
          isHydrated: sdk.isHydrated,
          hasError: Boolean(sdk.error),
        });
      } catch (error) {
        logger.error('Authenticated layout SDK initialize failed', {
          durationMs: Date.now() - startedAt,
          sdkState: sdk.state,
          isHydrated: sdk.isHydrated,
          error,
        });
        console.error(error);
      }
    };

    initialize();
    void userStore.refreshIfStale();
  }, [sdk]);

  useEffect(() => {
    const refreshOnReturn = async () => {
      await userStore.refreshIfStale();
      subscriptionApis.getStatus();
      refreshFeatureFlags();
    };

    // WKWebView may not fire `focus` when the app returns to the foreground;
    // `visibilitychange` is the reliable signal there. Both can fire in a
    // desktop browser and a double refresh is harmless.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refreshOnReturn();
    };

    window.addEventListener('focus', refreshOnReturn);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', refreshOnReturn);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useSetChatMode();
  useRegisterDevice();
  usePushNotifications();

  useEffect(() => {
    subscriptionApis.getStatus();
    subscriptionApis.getPricingPlans();
    void teamApis.fetchDashboard({ silent: true });
  }, []);

  useEffect(() => {
    updateStore.setUserVersionSupported(
      userStore.user?.client_version_supported ?? true
    );

    const dispose = reaction(
      () => userStore.user?.client_version_supported,
      (clientVersionSupported) => {
        updateStore.setUserVersionSupported(clientVersionSupported ?? true);
      }
    );

    return dispose;
  }, []);

  return (
    <div className="bg-background">
      {showSplash && !error ? (
        <motion.div
          key="splash"
          variants={fadeVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          role="status"
          aria-busy="true"
          className="h-screen w-screen"
        >
          <Splash
            reason="sdk-loading"
            animate
            context={{
              sdkState: sdk.state,
              isHydrated,
            }}
          />
        </motion.div>
      ) : error ? (
        <motion.div
          key="error"
          variants={fadeVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <ErrorScreen error={error} reset={() => undefined} />
        </motion.div>
      ) : (
        <MainContent />
      )}
      <FailedSavesBanner />
    </div>
  );
});
